import {
  Check as CheckIcon,
  Close as CloseIcon,
  FlashOn as EnergyIcon,
  FilterAlt as FilterIcon,
  AccountTree as JourneyIcon,
  Grade as PriorityIcon,
  AccountBalance as AutogiroIcon,
  EventBusy as DeliveryEndIcon,
  Receipt as ReceiptIcon,
  FormatSize as SizeIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Popover,
  Slider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, isValid, parseISO } from "date-fns";
import React from "react";
import { formatEnergyWithScaling } from "../../../utils/numberFormatters";
import { getDeliveryEndFilterYear } from "./dateUtils";
import { DELIVERY_END_REASONS } from "./deliveryEndReasons";
import {
  ActionFilter,
  ActivityFilter,
  ConsumptionFilter,
  ContactFilter,
  DeliveryEndFilter,
  DeliveryEndReasonFilter,
  DirectDebitFilter,
  InvoiceDeliveryFilter,
  JourneyTypeFilter,
  SegmentFilter,
  SegmentImportanceFilter,
  SegmentSizeFilter,
} from "./FilterButton";
import FilterShortcuts from "./FilterShortcuts";

interface FilterPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  segmentFilter: SegmentFilter;
  setSegmentFilter: (filter: SegmentFilter) => void;
  segmentImportanceFilter: SegmentImportanceFilter;
  setSegmentImportanceFilter: (filter: SegmentImportanceFilter) => void;
  segmentSizeFilter: SegmentSizeFilter;
  setSegmentSizeFilter: (filter: SegmentSizeFilter) => void;
  activityFilter: ActivityFilter;
  setActivityFilter: (filter: ActivityFilter) => void;
  actionFilter: ActionFilter;
  setActionFilter: (filter: ActionFilter) => void;
  contactFilter: ContactFilter;
  setContactFilter: (filter: ContactFilter) => void;
  uniqueSegments: string[];
  journeyTypeFilter?: JourneyTypeFilter;
  setJourneyTypeFilter?: (filter: JourneyTypeFilter) => void;
  uniqueJourneyTypes?: string[];
  consumptionFilter?: ConsumptionFilter;
  setConsumptionFilter?: (filter: ConsumptionFilter) => void;
  invoiceDeliveryFilter?: InvoiceDeliveryFilter;
  setInvoiceDeliveryFilter?: (filter: InvoiceDeliveryFilter) => void;
  directDebitFilter?: DirectDebitFilter;
  setDirectDebitFilter?: (filter: DirectDebitFilter) => void;
  deliveryEndFilter?: DeliveryEndFilter;
  setDeliveryEndFilter?: (filter: DeliveryEndFilter) => void;
  uniqueDeliveryEndYears?: number[];
  deliveryEndReasonFilter?: DeliveryEndReasonFilter;
  setDeliveryEndReasonFilter?: (filter: DeliveryEndReasonFilter) => void;
  onClearAll: () => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  segmentFilter,
  setSegmentFilter,
  segmentImportanceFilter,
  setSegmentImportanceFilter,
  segmentSizeFilter,
  setSegmentSizeFilter,
  activityFilter,
  setActivityFilter,
  actionFilter,
  setActionFilter,
  contactFilter,
  setContactFilter,
  uniqueSegments,
  journeyTypeFilter = null,
  setJourneyTypeFilter = () => {},
  uniqueJourneyTypes = [],
  consumptionFilter = "all",
  setConsumptionFilter = () => {},
  invoiceDeliveryFilter = null,
  setInvoiceDeliveryFilter = () => {},
  directDebitFilter = "all",
  setDirectDebitFilter = () => {},
  deliveryEndFilter = null,
  setDeliveryEndFilter = () => {},
  uniqueDeliveryEndYears = [],
  deliveryEndReasonFilter = null,
  setDeliveryEndReasonFilter = () => {},
  onClearAll,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Extract unique importance levels (A, B, C, etc.) from segments
  const uniqueImportanceLevels = React.useMemo(() => {
    const importanceLevels = uniqueSegments
      .map((segment) => segment.charAt(0))
      .filter(Boolean);
    return Array.from(new Set(importanceLevels)).sort();
  }, [uniqueSegments]);

  // Extract unique size levels (1, 2, 3, etc.) from segments
  const uniqueSizeLevels = React.useMemo(() => {
    const sizeLevels = uniqueSegments
      .map((segment) => segment.charAt(1))
      .filter((level) => /\d/.test(level));
    return Array.from(new Set(sizeLevels)).sort();
  }, [uniqueSegments]);

  // Slider state for custom interval
  const [sliderValue, setSliderValue] = React.useState<[number, number]>([
    0, 50000000,
  ]);

  // If filter is interval, sync slider
  React.useEffect(() => {
    if (typeof consumptionFilter === "object" && consumptionFilter !== null) {
      setSliderValue([consumptionFilter.min, consumptionFilter.max]);
    }
  }, [consumptionFilter]);

  // Count active filters
  const activeFilterCount = [
    segmentFilter !== null,
    segmentImportanceFilter !== null,
    segmentSizeFilter !== null,
    journeyTypeFilter !== null,
    consumptionFilter !== "all",
    activityFilter !== "all",
    actionFilter !== "all",
    contactFilter !== "all",
    invoiceDeliveryFilter !== null,
    directDebitFilter !== "all",
    deliveryEndFilter !== null,
    deliveryEndReasonFilter !== null,
  ].filter(Boolean).length;

  const selectedDeliveryEndYear = getDeliveryEndFilterYear(deliveryEndFilter);

  // Uppdatera en gräns i leveransslut-intervallet; båda tomma = inget filter
  const handleDeliveryEndDateChange = (
    bound: "from" | "to",
    date: Date | null
  ) => {
    // Ignorera ofullständiga datum medan användaren skriver
    if (date && !isValid(date)) return;
    const value = date ? format(date, "yyyy-MM-dd") : null;
    const next = {
      from: deliveryEndFilter?.from ?? null,
      to: deliveryEndFilter?.to ?? null,
      [bound]: value,
    };
    setDeliveryEndFilter(next.from || next.to ? next : null);
  };

  const filterContent = (
    <Box
      sx={{
        width: isMobile ? "100%" : 700,
        maxHeight: isMobile ? "calc(100vh - 100px)" : 700,
        overflowY: "auto",
        p: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          position: "sticky",
          top: -16,
          bgcolor: "background.paper",
          pt: 2,
          pb: 1,
          zIndex: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterIcon />
          Filter
          {activeFilterCount > 0 && (
            <Chip label={activeFilterCount} size="small" color="primary" />
          )}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="text"
            color="warning"
            onClick={onClearAll}
            disabled={activeFilterCount === 0}
          >
            Rensa alla
          </Button>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <FilterShortcuts />

      <Divider sx={{ my: 2 }} />

      {/* Two-column layout for desktop */}
      <Box
        sx={{
          display: isMobile ? "block" : "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? 0 : 3,
        }}
      >
        {/* Left Column - Segment & Customer Type Filters */}
        <Box>
          {/* Segment Filters */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Segment
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={segmentFilter === null ? "filled" : "outlined"}
                color={segmentFilter === null ? "primary" : "default"}
                onClick={() => setSegmentFilter(null)}
                size="small"
                icon={segmentFilter === null ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Saknar segment"
                variant={segmentFilter === "" ? "filled" : "outlined"}
                color={segmentFilter === "" ? "primary" : "default"}
                onClick={() => setSegmentFilter("")}
                size="small"
                icon={segmentFilter === "" ? <CheckIcon /> : undefined}
              />
              {uniqueSegments.map((segment) => (
                <Chip
                  key={segment}
                  label={segment}
                  variant={segmentFilter === segment ? "filled" : "outlined"}
                  color={segmentFilter === segment ? "primary" : "default"}
                  onClick={() => setSegmentFilter(segment)}
                  size="small"
                  icon={segmentFilter === segment ? <CheckIcon /> : undefined}
                />
              ))}
            </Stack>
          </Box>

          {/* Segment Importance Level */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <PriorityIcon fontSize="small" />
              Kundnivå
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={
                  segmentImportanceFilter === null ? "filled" : "outlined"
                }
                color={segmentImportanceFilter === null ? "primary" : "default"}
                onClick={() => setSegmentImportanceFilter(null)}
                size="small"
                icon={
                  segmentImportanceFilter === null ? <CheckIcon /> : undefined
                }
              />
              {uniqueImportanceLevels.map((level) => (
                <Chip
                  key={`importance-${level}`}
                  label={level}
                  variant={
                    segmentImportanceFilter === level ? "filled" : "outlined"
                  }
                  color={
                    segmentImportanceFilter === level ? "primary" : "default"
                  }
                  onClick={() => setSegmentImportanceFilter(level)}
                  size="small"
                  icon={
                    segmentImportanceFilter === level ? (
                      <CheckIcon />
                    ) : undefined
                  }
                />
              ))}
            </Stack>
          </Box>

          {/* Segment Size */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <SizeIcon fontSize="small" />
              Kundstorlek
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={segmentSizeFilter === null ? "filled" : "outlined"}
                color={segmentSizeFilter === null ? "primary" : "default"}
                onClick={() => setSegmentSizeFilter(null)}
                size="small"
                icon={segmentSizeFilter === null ? <CheckIcon /> : undefined}
              />
              {uniqueSizeLevels.map((level) => (
                <Chip
                  key={`size-${level}`}
                  label={level}
                  variant={segmentSizeFilter === level ? "filled" : "outlined"}
                  color={segmentSizeFilter === level ? "primary" : "default"}
                  onClick={() => setSegmentSizeFilter(level)}
                  size="small"
                  icon={segmentSizeFilter === level ? <CheckIcon /> : undefined}
                />
              ))}
            </Stack>
          </Box>

          {/* Journey Type */}
          {uniqueJourneyTypes.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="medium"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <JourneyIcon fontSize="small" />
                Kundtyp
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip
                  label="Alla"
                  variant={journeyTypeFilter === null ? "filled" : "outlined"}
                  color={journeyTypeFilter === null ? "primary" : "default"}
                  onClick={() => setJourneyTypeFilter(null)}
                  size="small"
                  icon={journeyTypeFilter === null ? <CheckIcon /> : undefined}
                />
                {uniqueJourneyTypes.map((journeyType) => (
                  <Chip
                    key={journeyType}
                    label={journeyType}
                    variant={
                      journeyTypeFilter === journeyType ? "filled" : "outlined"
                    }
                    color={
                      journeyTypeFilter === journeyType ? "primary" : "default"
                    }
                    onClick={() => setJourneyTypeFilter(journeyType)}
                    size="small"
                    icon={
                      journeyTypeFilter === journeyType ? (
                        <CheckIcon />
                      ) : undefined
                    }
                  />
                ))}
              </Stack>
            </Box>
          )}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Kontaktperson
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={contactFilter === "all" ? "filled" : "outlined"}
                color={contactFilter === "all" ? "primary" : "default"}
                onClick={() => setContactFilter("all")}
                size="small"
                icon={contactFilter === "all" ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Har kontaktperson"
                variant={
                  contactFilter === "has-contact" ? "filled" : "outlined"
                }
                color={contactFilter === "has-contact" ? "primary" : "default"}
                onClick={() => setContactFilter("has-contact")}
                size="small"
                icon={
                  contactFilter === "has-contact" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Saknar kontaktperson"
                variant={contactFilter === "no-contact" ? "filled" : "outlined"}
                color={contactFilter === "no-contact" ? "primary" : "default"}
                onClick={() => setContactFilter("no-contact")}
                size="small"
                icon={
                  contactFilter === "no-contact" ? <CheckIcon /> : undefined
                }
              />
            </Stack>
          </Box>

          {/* Invoice Delivery */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <ReceiptIcon fontSize="small" />
              Fakturaleverans
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={invoiceDeliveryFilter === null ? "filled" : "outlined"}
                color={invoiceDeliveryFilter === null ? "primary" : "default"}
                onClick={() => setInvoiceDeliveryFilter(null)}
                size="small"
                icon={invoiceDeliveryFilter === null ? <CheckIcon /> : undefined}
              />
              {[
                "Brev",
                "E-faktura",
                "SVE-Faktura",
                "Mailfaktura",
                "Endast PDF",
                "Lokal utskrift",
                "Internkrediterad",
                "Mailfaktura EUR",
                "Ej valt",
                "Kivra",
              ].map((option) => (
                <Chip
                  key={option}
                  label={option}
                  variant={invoiceDeliveryFilter === option ? "filled" : "outlined"}
                  color={invoiceDeliveryFilter === option ? "primary" : "default"}
                  onClick={() => setInvoiceDeliveryFilter(option)}
                  size="small"
                  icon={invoiceDeliveryFilter === option ? <CheckIcon /> : undefined}
                />
              ))}
            </Stack>
          </Box>

          {/* Direct Debit (Autogiro) */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <AutogiroIcon fontSize="small" />
              Autogiro
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={directDebitFilter === "all" ? "filled" : "outlined"}
                color={directDebitFilter === "all" ? "primary" : "default"}
                onClick={() => setDirectDebitFilter("all")}
                size="small"
                icon={directDebitFilter === "all" ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Autogiro"
                variant={directDebitFilter === "yes" ? "filled" : "outlined"}
                color={directDebitFilter === "yes" ? "primary" : "default"}
                onClick={() => setDirectDebitFilter("yes")}
                size="small"
                icon={directDebitFilter === "yes" ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Ej autogiro"
                variant={directDebitFilter === "no" ? "filled" : "outlined"}
                color={directDebitFilter === "no" ? "primary" : "default"}
                onClick={() => setDirectDebitFilter("no")}
                size="small"
                icon={directDebitFilter === "no" ? <CheckIcon /> : undefined}
              />
            </Stack>
          </Box>
        </Box>

        {/* Right Column - Consumption & Activity Filters */}
        <Box>
          {/* Consumption Filters */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <EnergyIcon fontSize="small" />
              Förbrukning
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
            >
              <Chip
                label="Alla"
                variant={consumptionFilter === "all" ? "filled" : "outlined"}
                color={consumptionFilter === "all" ? "primary" : "default"}
                onClick={() => setConsumptionFilter("all")}
                size="small"
                icon={consumptionFilter === "all" ? <CheckIcon /> : undefined}
              />
              <Chip
                label={`Låg (<${formatEnergyWithScaling(100000)})`}
                variant={consumptionFilter === "low" ? "filled" : "outlined"}
                color={consumptionFilter === "low" ? "primary" : "default"}
                onClick={() => setConsumptionFilter("low")}
                size="small"
                icon={consumptionFilter === "low" ? <CheckIcon /> : undefined}
              />
              <Chip
                label={`Medel`}
                variant={consumptionFilter === "medium" ? "filled" : "outlined"}
                color={consumptionFilter === "medium" ? "primary" : "default"}
                onClick={() => setConsumptionFilter("medium")}
                size="small"
                icon={
                  consumptionFilter === "medium" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label={`Hög (>${formatEnergyWithScaling(500000)})`}
                variant={consumptionFilter === "high" ? "filled" : "outlined"}
                color={consumptionFilter === "high" ? "primary" : "default"}
                onClick={() => setConsumptionFilter("high")}
                size="small"
                icon={consumptionFilter === "high" ? <CheckIcon /> : undefined}
              />
            </Stack>

            {/* Custom interval slider */}
            <Box sx={{ px: 1, bgcolor: "grey.50", borderRadius: 1, py: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Anpassat intervall (kWh):
              </Typography>
              <Slider
                value={sliderValue}
                min={0}
                max={50000000}
                step={10000}
                onChange={(_, newValue) =>
                  setSliderValue(newValue as [number, number])
                }
                onChangeCommitted={(_, newValue) => {
                  const [min, max] = newValue as [number, number];
                  setConsumptionFilter({ min, max });
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => formatEnergyWithScaling(value)}
                disableSwap
                size="small"
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption">
                  {formatEnergyWithScaling(sliderValue[0])}
                </Typography>
                <Typography variant="caption">
                  {formatEnergyWithScaling(sliderValue[1])}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Activity Actions */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Aktiviteter
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={actionFilter === "all" ? "filled" : "outlined"}
                color={actionFilter === "all" ? "primary" : "default"}
                onClick={() => setActionFilter("all")}
                size="small"
                icon={actionFilter === "all" ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Har nästa"
                variant={
                  actionFilter === "has-next-action" ? "filled" : "outlined"
                }
                color={
                  actionFilter === "has-next-action" ? "primary" : "default"
                }
                onClick={() => setActionFilter("has-next-action")}
                size="small"
                icon={
                  actionFilter === "has-next-action" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Saknar nästa"
                variant={
                  actionFilter === "no-next-action" ? "filled" : "outlined"
                }
                color={
                  actionFilter === "no-next-action" ? "primary" : "default"
                }
                onClick={() => setActionFilter("no-next-action")}
                size="small"
                icon={
                  actionFilter === "no-next-action" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Haft senaste 6 månaderna"
                variant={
                  actionFilter === "last-action-6m" ? "filled" : "outlined"
                }
                color={
                  actionFilter === "last-action-6m" ? "primary" : "default"
                }
                onClick={() => setActionFilter("last-action-6m")}
                size="small"
                icon={
                  actionFilter === "last-action-6m" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Haft senaste 3 månaderna"
                variant={
                  actionFilter === "last-action-3m" ? "filled" : "outlined"
                }
                color={
                  actionFilter === "last-action-3m" ? "primary" : "default"
                }
                onClick={() => setActionFilter("last-action-3m")}
                size="small"
                icon={
                  actionFilter === "last-action-3m" ? <CheckIcon /> : undefined
                }
              />
            </Stack>
          </Box>

          {/* Login Activity */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Inloggning
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={activityFilter === "all" ? "filled" : "outlined"}
                color={activityFilter === "all" ? "primary" : "default"}
                onClick={() => setActivityFilter("all")}
                size="small"
                icon={activityFilter === "all" ? <CheckIcon /> : undefined}
              />
              <Chip
                label="Har loggat in"
                variant={activityFilter === "logged-in" ? "filled" : "outlined"}
                color={activityFilter === "logged-in" ? "primary" : "default"}
                onClick={() => setActivityFilter("logged-in")}
                size="small"
                icon={
                  activityFilter === "logged-in" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Senaste 7 dagar"
                variant={activityFilter === "logged-in-7d" ? "filled" : "outlined"}
                color={activityFilter === "logged-in-7d" ? "primary" : "default"}
                onClick={() => setActivityFilter("logged-in-7d")}
                size="small"
                icon={
                  activityFilter === "logged-in-7d" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Senaste 30 dagar"
                variant={activityFilter === "logged-in-30d" ? "filled" : "outlined"}
                color={activityFilter === "logged-in-30d" ? "primary" : "default"}
                onClick={() => setActivityFilter("logged-in-30d")}
                size="small"
                icon={
                  activityFilter === "logged-in-30d" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Senaste 90 dagar"
                variant={activityFilter === "logged-in-90d" ? "filled" : "outlined"}
                color={activityFilter === "logged-in-90d" ? "primary" : "default"}
                onClick={() => setActivityFilter("logged-in-90d")}
                size="small"
                icon={
                  activityFilter === "logged-in-90d" ? <CheckIcon /> : undefined
                }
              />
              <Chip
                label="Aldrig inloggad"
                variant={activityFilter === "no-login" ? "filled" : "outlined"}
                color={activityFilter === "no-login" ? "primary" : "default"}
                onClick={() => setActivityFilter("no-login")}
                size="small"
                icon={activityFilter === "no-login" ? <CheckIcon /> : undefined}
              />
            </Stack>
          </Box>

          {/* Delivery End Date */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              fontWeight="medium"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <DeliveryEndIcon fontSize="small" />
              Leveransslut
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
            >
              <Chip
                label="Alla"
                variant={deliveryEndFilter === null ? "filled" : "outlined"}
                color={deliveryEndFilter === null ? "primary" : "default"}
                onClick={() => setDeliveryEndFilter(null)}
                size="small"
                icon={deliveryEndFilter === null ? <CheckIcon /> : undefined}
              />
              {uniqueDeliveryEndYears.map((year) => (
                <Chip
                  key={`delivery-end-${year}`}
                  label={year}
                  variant={selectedDeliveryEndYear === year ? "filled" : "outlined"}
                  color={selectedDeliveryEndYear === year ? "primary" : "default"}
                  onClick={() =>
                    setDeliveryEndFilter({
                      from: `${year}-01-01`,
                      to: `${year}-12-31`,
                    })
                  }
                  size="small"
                  icon={selectedDeliveryEndYear === year ? <CheckIcon /> : undefined}
                />
              ))}
            </Stack>

            {/* Custom interval */}
            <Box sx={{ px: 1, bgcolor: "grey.50", borderRadius: 1, py: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Anpassat intervall:
              </Typography>
              <Stack direction="row" spacing={1}>
                <DatePicker
                  label="Från"
                  value={
                    deliveryEndFilter?.from ? parseISO(deliveryEndFilter.from) : null
                  }
                  onChange={(date) => handleDeliveryEndDateChange("from", date)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
                <DatePicker
                  label="Till"
                  value={
                    deliveryEndFilter?.to ? parseISO(deliveryEndFilter.to) : null
                  }
                  onChange={(date) => handleDeliveryEndDateChange("to", date)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                    field: { clearable: true },
                  }}
                />
              </Stack>
            </Box>

            {/* Delivery end reason */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Orsak:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                label="Alla"
                variant={deliveryEndReasonFilter === null ? "filled" : "outlined"}
                color={deliveryEndReasonFilter === null ? "primary" : "default"}
                onClick={() => setDeliveryEndReasonFilter(null)}
                size="small"
                icon={deliveryEndReasonFilter === null ? <CheckIcon /> : undefined}
              />
              {DELIVERY_END_REASONS.map((reason) => (
                <Chip
                  key={`delivery-end-reason-${reason.id}`}
                  label={reason.name}
                  variant={deliveryEndReasonFilter === reason.id ? "filled" : "outlined"}
                  color={deliveryEndReasonFilter === reason.id ? "primary" : "default"}
                  onClick={() => setDeliveryEndReasonFilter(reason.id)}
                  size="small"
                  icon={deliveryEndReasonFilter === reason.id ? <CheckIcon /> : undefined}
                />
              ))}
            </Stack>
          </Box>

          {/* Contact Person */}
        </Box>
      </Box>

      {/* Apply button for mobile */}
      {isMobile && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            pt: 2,
            pb: 1,
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={onClose}
            startIcon={<CheckIcon />}
          >
            Applicera filter
          </Button>
        </Box>
      )}
    </Box>
  );

  // Desktop: Popover
  if (!isMobile) {
    return (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            borderRadius: 2,
          },
        }}
      >
        {filterContent}
      </Popover>
    );
  }

  // Mobile: Bottom Drawer
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "90vh",
        },
      }}
    >
      {/* Swipe handle indicator */}
      <Box
        sx={{
          width: 40,
          height: 4,
          bgcolor: "grey.300",
          borderRadius: 2,
          mx: "auto",
          my: 1,
        }}
      />
      {filterContent}
    </Drawer>
  );
};

export default FilterPopover;
